import { DEFAULT_LOCALE, isLocale, resolveLocale, type Locale } from '@telestore/i18n';

/**
 * Which language the admin opens in, before any session or shop exists.
 *
 * Three candidates, in the order of how much they tell us about what this
 * merchant wants right now:
 *
 *   1. `?lang=` on the URL. The marketing landing puts it on every call to
 *      action (§6.3), so it says what they were reading a click ago. Until
 *      this existed the funnel threw that away and an English reader landed
 *      on a Khmer sign-in.
 *   2. What they chose here before. A stored preference is a button they
 *      pressed, so it outranks anything inferred.
 *   3. Their Telegram language. Only ever present in the Mini App, where
 *      there is no link to carry `?lang=` — which is why an English-speaking
 *      merchant used to get Khmer inside Telegram and English on the web, from
 *      the same account. Telegram tells us; we were not listening.
 *
 * Ranked deliberately below the stored value: opening the app inside Telegram
 * must not silently undo the language toggle they pressed on their last visit.
 */
export function initialLocale(
  search: string,
  stored: string | null,
  telegramLanguageCode?: string | null,
): Locale {
  const requested = new URLSearchParams(search).get('lang');
  if (isLocale(requested)) return requested;
  if (isLocale(stored)) return stored;

  // `resolveLocale` handles the region suffixes Telegram sends (`km-KH`) and
  // falls back for languages we do not have.
  return resolveLocale({ telegramLanguageCode, shopDefault: DEFAULT_LOCALE });
}
